// The in-progress inspection, kept on disk so it survives losing the page.
//
// The checklist and transcript live in React state, which means they die with
// the component - and the component dies whenever the app returns to the
// sign-in screen. A token revoked mid-call therefore costs the inspector the
// call AND everything the call had established, which is the worst version of
// a sign-out bug: the customer has already said the things, and nobody is
// going to ask them again.
//
// So the working state is written down as it changes and restored on load.
// This is a draft, not a record: /inspections is where a call is actually
// saved. This exists purely so a re-login, a reload, or a crashed tab does not
// throw away work that has not reached the endpoint yet.

import type { FieldOverride, FieldState } from './extraction';
import type { TranscriptSegment } from './Transcriber';

const DRAFT_KEY = 'virtual_inspection.draft.v1';

/**
 * How long a draft is worth restoring.
 *
 * Long enough to outlast a sign-out and a re-login, short enough that last
 * week's abandoned call does not reappear beside a fresh one. A draft older
 * than this is almost certainly a call somebody walked away from.
 */
const MAX_AGE_MS = 12 * 60 * 60 * 1000;

export type CallDraft = {
  callId: string;
  /** When the call was joined, so a restored draft can still be saved truthfully. */
  joinedAt: string | null;
  elapsedMs: number;
  state: FieldState[];
  /** Serialised as pairs: a Map does not survive JSON. */
  overrides: [string, FieldOverride][];
  segments: TranscriptSegment[];
  savedAt: string;
};

export function saveDraft(draft: Omit<CallDraft, 'savedAt'>): void {
  try {
    // Nothing to keep. Writing an empty draft would only give a later restore
    // something meaningless to offer.
    if (!draft.callId || (draft.state.length === 0 && draft.segments.length === 0)) return;
    window.localStorage.setItem(
      DRAFT_KEY,
      JSON.stringify({ ...draft, savedAt: new Date().toISOString() }),
    );
  } catch {
    // Storage full or disabled. A draft is a convenience; the call carries on.
  }
}

export function loadDraft(): CallDraft | null {
  try {
    const raw = window.localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const draft = JSON.parse(raw) as CallDraft;
    if (!draft?.callId || !Array.isArray(draft.state)) return null;

    const age = Date.now() - new Date(draft.savedAt).getTime();
    if (!Number.isFinite(age) || age > MAX_AGE_MS) {
      clearDraft();
      return null;
    }
    return draft;
  } catch {
    return null;
  }
}

export function clearDraft(): void {
  try {
    window.localStorage.removeItem(DRAFT_KEY);
  } catch {
    /* nothing to clear */
  }
}

/** The Map the panel works in, back from the pairs a draft stores. */
export function draftOverrides(draft: CallDraft): Map<string, FieldOverride> {
  return new Map(draft.overrides ?? []);
}
